import { useEffect, useMemo, useRef, useState, FC } from 'react'
import { useGameContext } from '@/screens/GameContainer/GameContext'
import { useHandHistoryQuery } from './useGameState'
import {
  getPotDistributionWinners,
  InnerWinner
} from '@/components/ANewDesign/BottomModalContent/Content/Chats/ChatWinner'
import { Doc } from '@ppwr/convex/_generated/dataModel'
import { ChatKind, MessageContractModel, MessageContractModelType } from '../../models/api-store'
import { mockCompletedHand1 } from '../../utils/mockHandHistory'

// TODO extract to common utils hooks file
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>(undefined)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current //in the end, return the current ref value.
}

//#region hooks
type HandWinnersResult = UseLatestWinningHandResult & {
  isUserWinner: boolean
  userWinner: InnerWinner | null
  winners: InnerWinner[]
}

export function useHandWinners(): HandWinnersResult {
  const { userId } = useGameContext() // Use arg instead?
  const { isNewHand, handNumber, latestHand } = useLatestWinningHand()

  // Calculate winners synchronously, not in useEffect
  // This ensures isUserWinner is computed in the same render as isNewHand
  const { winners, userWinner, isUserWinner } = useMemo(() => {
    if (isNewHand && latestHand) {
      const winners = getPotDistributionWinners(latestHand)
      const userMatch = winners.find((w) => w.userId === userId)

      !!userMatch && console.log('useHandWinners - the current user is a winner in the new hand!')

      return {
        winners,
        userWinner: userMatch || null,
        isUserWinner: !!userMatch
      }
    }

    // Return empty state if no new hand
    return {
      winners: [],
      userWinner: null,
      isUserWinner: false
    }
  }, [isNewHand, latestHand, userId])

  return { isNewHand, handNumber, latestHand, isUserWinner, userWinner, winners }
}

type UseLatestWinningHandResult = {
  isNewHand: boolean
  handNumber: number | undefined
  latestHand: Doc<'handHistory'> | null
}

export function useLatestWinningHand(mockHandHistory?: Doc<'handHistory'>[]): UseLatestWinningHandResult {
  const { gameId } = useGameContext()
  const { handHistory } = !mockHandHistory ? useHandHistoryQuery(gameId) : { handHistory: mockHandHistory ?? [] }

  // handHistory is in descending order, so [0] is the latest hand
  const currentHandNumber = handHistory?.[0]?.handNumber
  const prevHandNumber = usePrevious(currentHandNumber)

  const latestHand = useMemo(() => handHistory?.[0] ?? null, [handHistory?.[0]])
  const isNewHand = useMemo(() => {
    // Detect new hand by comparing handNumber (not array length, since array is limited to 15)
    // Only trigger if we had a previous handNumber AND the current one is greater
    return currentHandNumber !== undefined && currentHandNumber > (prevHandNumber || 0)
    // return prevHandNumber !== undefined && currentHandNumber !== undefined && currentHandNumber > prevHandNumber
  }, [currentHandNumber, prevHandNumber])

  return { latestHand, isNewHand, handNumber: currentHandNumber }
}

//#endregion

//#region HookTester component for testing usePrevious and useLatestWinningHand
export const HookTester: FC<{ isPaused: boolean }> = ({ isPaused }) => {
  const prevIsPaused = usePrevious(isPaused)

  // Simulate starting with empty array, then adding a hand after 2 seconds
  const [mockHands, setMockHands] = useState<Doc<'handHistory'>[]>([])

  useEffect(() => {
    console.log('HookTester::Mounted, starting with empty array')
    const timer = setTimeout(() => {
      console.log('HookTester::Adding 1 mock hand to array')
      setMockHands([mockCompletedHand1])
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  const { latestHand, isNewHand } = useLatestWinningHand(mockHands)

  useEffect(() => {
    if (isNewHand && latestHand) {
      console.log('HookTester::✅ New winning hand detected:', { isNewHand, handNumber: latestHand.handNumber })
      // Do something with the new hand
    } else {
      console.log('HookTester::⏸️ No new hand detected.', { isNewHand, handNumber: latestHand?.handNumber })
    }
  }, [isNewHand, latestHand])

  useEffect(() => {
    if (prevIsPaused !== undefined && isPaused !== prevIsPaused) {
      console.log(`HookTester::'isPaused' changed from ${prevIsPaused} to ${isPaused}`)
    }
  }, [isPaused, prevIsPaused])

  return null
}
//#endregion
